import { useCallback, useMemo } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'

import {
  Message,
  TokenDisplay,
  TokenSelector,
} from '~/components/component-library'
import { EarnFormValues } from '~/components/forms/earn/earnSchema'
import { useAggregatedBalances } from '~/hooks/useAggregatedBalances'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { Address } from '~/utils/address'
import { isVaultAssetPair } from '~/utils/vault'

export const EarnDepositSection = ({
  onInsufficientBalanceChange,
}: {
  onInsufficientBalanceChange: (insufficient: boolean) => void
}) => {
  const { control } = useFormContext<EarnFormValues>()

  const account = useWatch({ control, name: 'account' })

  const sellToken = useWatch({ control, name: 'swapData.sellToken.token' })
  const sellTokenChainId = useWatch({
    control,
    name: 'swapData.sellToken.chainId',
  })

  const assetToken = useWatch({ control, name: 'asset.token' })
  const assetTokenChainId = useWatch({
    control,
    name: 'asset.chainId',
  })

  const vaultToken = useWatch({ control, name: 'vault.token' })
  const vaultTokenChainId = useWatch({
    control,
    name: 'vault.chainId',
  })

  const sellTokenMetadata = useTokenMetadata({
    address: sellToken,
    chainId: sellTokenChainId,
  })
  const assetTokenMetadata = useTokenMetadata({
    address: assetToken,
    chainId: assetTokenChainId,
  })

  const { balances } = useAggregatedBalances({
    filterAccountAddresses: useMemo(
      () => (account ? [account] : undefined),
      [account],
    ),
  })

  const balanceTokenKeys = useMemo(
    () =>
      new Set(balances.map((b) => `${b.chainId}-${b.address.toLowerCase()}`)),
    [balances],
  )

  const chainFilter = useCallback(
    (id: number) => vaultTokenChainId === id,
    [vaultTokenChainId],
  )

  const tokenFilter = useCallback(
    ({ address, chainId }: { address: string; chainId: number }) => {
      const hasBalance = balanceTokenKeys.has(
        `${chainId}-${address.toLowerCase()}`,
      )
      return hasBalance && !Address.equal(address, vaultToken)
    },
    [balanceTokenKeys, vaultToken],
  )

  return (
    <div className="space-y-2">
      <TokenSelector<EarnFormValues>
        control={control}
        label="Deposit amount"
        addressInputName="swapData.sellToken.token"
        chainIdInputName="swapData.sellToken.chainId"
        amountInputName="swapData.sellToken.amount"
        filterAccountAddress={account}
        showBalances
        sortBy="value"
        chainFilter={chainFilter}
        tokenFilter={tokenFilter}
        onExceedsBalanceChange={onInsufficientBalanceChange}
      />
      {sellToken &&
        sellTokenChainId &&
        assetToken &&
        assetTokenChainId &&
        !isVaultAssetPair({
          chainId: vaultTokenChainId,
          vault: vaultToken,
          asset: sellToken,
        }) &&
        sellTokenMetadata &&
        assetTokenMetadata && (
          <Message variant="info">
            <span>
              <TokenDisplay
                address={sellToken}
                chainId={sellTokenChainId}
                layout="inline-text"
              />
              <span> will be sold for </span>
              <TokenDisplay
                address={assetToken}
                chainId={assetTokenChainId}
                layout="inline-text"
              />
              <span> and deposited into the vault</span>
            </span>
          </Message>
        )}
    </div>
  )
}
